import callApi from '../../utils/api/callAPI';

// Mã lỗi nghiệp vụ trong body lỗi của backend (đi kèm HTTP status tương ứng)
export const CUSTOMER_ERROR = {
  NOT_FOUND: 1404,
  ALREADY_CONFIRMED_OR_NOT: 1409,
};

const unwrap = res => {
  const response = res.data;
  if (!response?.success) throw response;
  return response.result?.data;
};

/**
 * Lỗi 409 của /confirm (đã chốt / chưa chốt) và của PUT room/<id>/customer (khách đang gắn với room đã chốt):
 * callApi ném nguyên axios response nên status nằm ở error.status.
 */
export const isConflictError = error => error?.status === 409;
export const isNotFoundError = error => error?.status === 404;

/**
 * @param {Object} [params]
 * @param {string} [params.search] Tìm gần đúng theo tên, SĐT hoặc email
 * @param {boolean} [params.isConfirmed]
 * @param {number} [params.limit] Mặc định 50 ở backend
 * @param {number} [params.offset]
 * @returns {Promise<{ customers: Object[], total: number }>}
 */
export const fetchCustomers = async ({
  search,
  isConfirmed,
  limit,
  offset,
} = {}) => {
  const res = await callApi({
    endpoint: 'customers/v3/customers',
    method: 'get',
    params: { search, is_confirmed: isConfirmed, limit, offset },
  });
  const data = unwrap(res);
  return {
    customers: data?.customers ?? [],
    total: res.data.count ?? data?.total ?? data?.customers?.length ?? 0,
  };
};

/** Tạo khách ảo. Chỉ `name` bắt buộc. */
export const createCustomer = async ({ name, phone, email, address, note }) => {
  const res = await callApi({
    endpoint: 'customers/v3/customers',
    method: 'post',
    data: { name, phone, email, address, note },
  });
  return unwrap(res);
};

export const fetchCustomer = async id =>
  unwrap(await callApi({ endpoint: `customers/v3/customers/${id}` }));

/** Gửi field nào sửa field đó. Không sửa được trạng thái chốt, dùng confirmCustomer/cancelCustomerConfirm. */
export const updateCustomer = async (id, fields) =>
  unwrap(
    await callApi({
      endpoint: `customers/v3/customers/${id}`,
      method: 'put',
      data: fields,
    })
  );

/** Chốt giá. 409 nếu khách đã chốt. */
export const confirmCustomer = async id =>
  unwrap(
    await callApi({
      endpoint: `customers/v3/customers/${id}/confirm`,
      method: 'post',
    })
  );

/** Huỷ chốt. 409 nếu khách chưa chốt. */
export const cancelCustomerConfirm = async id =>
  unwrap(
    await callApi({
      endpoint: `customers/v3/customers/${id}/confirm`,
      method: 'delete',
    })
  );

/** @returns {Promise<{id, action: 'confirm'|'cancel', price_version, actor_id, created_at}[]>} mới nhất trước */
export const fetchCustomerHistory = async id =>
  unwrap(await callApi({ endpoint: `customers/v3/customers/${id}/history` })) ??
  [];

/** Room của khách, của mọi người tạo. @returns {Promise<{id, name, thumbnail, creator_id, created_at, updated_at}[]>} */
export const fetchCustomerRooms = async id =>
  unwrap(await callApi({ endpoint: `customers/v3/customers/${id}/rooms` })) ??
  [];

/**
 * Gắn khách vào room, `customerId = null` để gỡ. Trả chi tiết room kèm customer_id, customer_name,
 * customer_is_confirmed. 409 nếu khách hiện tại của room đã chốt (phải huỷ chốt trước).
 */
export const setRoomCustomer = async (roomId, customerId) =>
  unwrap(
    await callApi({
      endpoint: `rooms/v3/room/${roomId}/customer`,
      method: 'put',
      data: { customer_id: customerId },
    })
  );
